import React from "react";
import { Box, Grid, Paper } from "@material-ui/core";
import "./Table.css";

import MaterialTable, {
  MTableGroupbar,
  MTableBodyRow,
  MTableHeader,
} from "@material-table/core";
import Pagination from "@mui/material/Pagination";
import { PaginationItem } from "@mui/material";

const noResults = "No results!";

const Table = React.forwardRef((props, ref) => {
  let [isLoading, setIsLoading] = React.useState(!!props.isLoading);
  let [grouping, setGrouping] = React.useState(!!props.grouping);
  let [paging, setPaging] = React.useState(props.paging ?? true);
  let [selection, setSelection] = React.useState(!!props.selection);
  let [defaultExpanded, setDefaultExpanded] = React.useState(!!props.defaultExpanded);
  let [limit, setLimit] = React.useState(props.limit ? props.limit : 999);
  let [total, setTotal] = React.useState(props.total ? props.total : 0);
  let [page, setPage] = React.useState(props.page ? props.page : 1);
  let [data, setData] = React.useState(
    props.data && props.data.length > 0 ? props.data : []
  );
  let [columns, setColumns] = React.useState(
    props.columns && props.columns.length > 0 ? props.columns : []
  );
  let [emptyText, setEmptyText] = React.useState(
    props.emptyText ? props.emptyText : noResults
  );
  let [maxBodyHeight, setMaxBodyHeight] = React.useState(
    props.maxBodyHeight ? props.maxBodyHeight : ""
  );
  let [minBodyHeight, setMinBodyHeight] = React.useState(
    props.minBodyHeight ? props.minBodyHeight : ""
  );
  let [tableLayout, setTableLayout] = React.useState(
    props.tableLayout ? props.tableLayout : "auto"
  );
  let [className] = React.useState(props.className ? props.className : "");

  React.useEffect(() => {
    setIsLoading(props.isLoading ?? false);

    setGrouping(props.grouping ?? false);

    setPaging(props.paging ?? true);

    setSelection(props.selection ?? false);

    setDefaultExpanded(props.defaultExpanded ?? false);

    setLimit(props.limit ? props.limit : 10);
    setTotal(props.total ? props.total : 0);
    setPage(props.page ? props.page : 1);
    setData(props.data?.length > 0 ? props.data : []);
    setColumns(props.columns?.length > 0 ? props.columns : []);
    setEmptyText(props.emptyText ? props.emptyText : noResults);
    setMaxBodyHeight(props.maxBodyHeight ? props.maxBodyHeight : "");
    setMinBodyHeight(props.minBodyHeight ? props.minBodyHeight : "");
    setTableLayout(props.tableLayout ? props.tableLayout : "");
  }, [
    props.isLoading,
    props.grouping,
    props.paging,
    props.limit,
    props.total,
    props.page,
    props.data,
    props.columns,
    props.emptyText,
    props.selection,
    props.maxBodyHeight,
    props.minBodyHeight,
    props.tableLayout,
    props.defaultExpanded,
  ]);
  const totalPages = Math.ceil(total / limit);

  return (
    <Box className={"custom-table-component " + className}>
      <MaterialTable
        tableRef={ref}
        components={{
          Container: (p) => {
            return (
              <Paper
                className={"custom-container"}
                elevation={0}
                square
                {...p}
              />
            );
          },
          Toolbar: (p) => {
            if (props.Toolbar) {
              return <Box>{props.Toolbar(p)}</Box>;
            } else {
              return null;
            }
          },
          Header: (p) => {
            if (props.Header) {
              return <React.Fragment>{props.Header(p)}</React.Fragment>;
            } else {
              return <MTableHeader {...p} />;
            }
          },
          Row: (p) => {
            // console.log(p);

            if (props.Row) {
              return (
                <React.Fragment>
                  <MTableBodyRow {...p} />
                  {props.Row(p, p.data)}
                </React.Fragment>
              );
            } else {
              return <MTableBodyRow {...p} />;
            }
          },
          Groupbar: (p) => {
            return (
              <Box className={"custom-groupbar"}>
                <MTableGroupbar {...p} />
              </Box>
            );
          },
          Pagination: (p) => {
            if (totalPages <= 1) {
              return null;
            }
            return (
              <td style={{ display: "flex", justifyContent: "center" }}>
                <Box pt={3}>
                  <Grid container>
                    <Box clone>
                      <Grid item xs={12} md={"auto"}>
                        <Pagination
                          showFirstButton
                          showLastButton
                          count={Math.ceil(total / limit)}
                          hideNextButton
                          hidePrevButton
                          size="small"
                          page={page}
                          color="primary"
                          onChange={(e, p) => {
                            if (props.onPageChange) {
                              props.onPageChange(p);
                            }
                          }}
                          renderItem={(item) => {
                            if (item.type === "first") {
                              return (
                                <button
                                  className="paginateButton"
                                  onClick={() => props.onPageChange(1)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') { 
                                      props.onPageChange(1);
                                    }
                                  }}
                                >First
                                </button>
                              );
                            }
                            if (item.type === "last") {
                              return (
                                <button
                                  className="paginateButton"
                                  type="button"
                                  onClick={() => props.onPageChange(item.page)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') { 
                                      props.onPageChange(item.page);
                                    }
                                  }}
                                >
                                  Last
                                </button>
                              );
                            } else {
                              return (
                                <PaginationItem
                                  {...item}
                                  onClick={(event) =>
                                    props.onPageChange(item.page)
                                  }
                                />
                              );
                            }
                          }}
                        />
                      </Grid>
                    </Box>
                  </Grid>
                </Box>
              </td>
            );
          },
        }}
        localization={{
          body: {
            emptyDataSourceMessage: emptyText,
          },
        }}
        options={{
          pageSize: limit,
          grouping: grouping,
          paging: paging,
          selection: selection,
          maxBodyHeight: maxBodyHeight,
          minBodyHeight: minBodyHeight,
          tableLayout: tableLayout,
          defaultExpanded: defaultExpanded,
        }}
        onSelectionChange={(rows) => {
          if (props.onSelectionChange) {
            return props.onSelectionChange(rows);
          }
        }}
        isLoading={isLoading}
        columns={columns}
        data={data}
        detailPanel={
          props.detailPanel
            ? [
              {
                render: (rowData) => {
                  if (props.detailPanel) {
                    return props.detailPanel(rowData);
                  }
                },
              },
            ]
            : null
        }
      />
    </Box>
  );
});

export default Table;
